import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  createCustomerFailure,
  createCustomerSuccess,
  onboardEmployeeFailure,
  onboardEmployeeSuccess,
  setAllowanceFailure,
  setAllowanceSuccess,
  setPermissionsFailure,
  setPermissionsSuccess,
  updatePeriodInfoFailure,
  updatePeriodInfoSuccess,
  uploadDocumentFailure,
  uploadDocumentSuccess,
  updateBasicInfoFailure,
  updateBasicInfoSuccess,
  setOnboardingData,
  setOnboardingInfo,
  getDocumentsSuccess,
  getDocumentsFailure,
  uploadCommonDocumentSuccess,
  uploadCommonDocumentFailure,
  getCommonDocumentsSuccess,
  getCommonDocumentsFailure,
  downloadDocSuccess,
  downloadDocFailure,
} from "./actions";
import {
  CREATE_CUSTOMER,
  ONBOARD_EMPLOYEE,
  SET_ALLOWANCE,
  SET_PERMISSIONS,
  UPDATE_PERIOD_INFO,
  UPLOAD_DOCUMENT,
  UPDATE_BASIC_INFO,
  GET_DOCUMENTS,
  UPLOAD_COMMON_DOCUMENT,
  GET_COMMON_DOCUMENTS,
  DOWNLOAD_DOC,
} from "./constants";

export function* createCustomerSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/onboarding/create`,
      payload,
    );
    yield put(
      createCustomerSuccess({
        documents: data.form_steps,
      }),
    );
    callback && callback(data);
    yield put(setOnboardingInfo(data));
  } catch (e) {
    yield put(createCustomerFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while creating customer!"));
  }
}

export function* uploadDocumentSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/hr-upload-document`,
      payload,
    );
    yield put(uploadDocumentSuccess(data));
    callback && callback(data);
  } catch (e) {
    yield put(uploadDocumentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while uploading document!"));
  }
}

export function* updatePeriodInfoSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/update/employement-period-info`,
      payload,
    );
    yield put(updatePeriodInfoSuccess(data));
    yield put(successAlert("Period info updated successfully!"));
    callback && callback();
  } catch (e) {
    yield put(updatePeriodInfoFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating period info!"));
  }
}

export function* setPermissionsSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/update/permissions`,
      payload,
    );
    yield put(setPermissionsSuccess(data));
    callback && callback();
    yield put(successAlert("Permissions set successfully!"));
  } catch (e) {
    yield put(setPermissionsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while setting permissions!"));
  }
}

export function* setAllowanceSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/update/holiday-allocation`,
      payload,
    );
    yield put(setAllowanceSuccess(data));
    yield put(successAlert("Allowance set successfully!"));
  } catch (e) {
    yield put(setAllowanceFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while setting allowance!"));
  }
}

export function* onboardEmployeeSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/onboarding/invite/${payload}`,
    );
    yield put(onboardEmployeeSuccess(data));
    yield put(
      successAlert("Employee onboarding invitation sent successfully!"),
    );
    typeof callback === "function" && callback();
  } catch (e) {
    yield put(onboardEmployeeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while onboarding employee!"));
  }
}

export function* updateBasicInfoSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/v2/employee/update/basic-info`,
      payload,
    );
    yield put(updateBasicInfoSuccess());
    yield put(successAlert("Basic info updated successfully!"));
    callback && callback();
  } catch (e) {
    yield put(updateBasicInfoFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating basic info!"));
  }
}

export function* getDocumentsSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { onboardingInfo } = yield select((state) => state?.OnboardingReducer);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/v2/employee/onboarding/document/${payload}`,
    );
    yield put(
      getDocumentsSuccess({
        documents: data?.form_steps ?? [],
        files: data?.hr_upload_document ?? [],
      }),
    );
    yield put(
      setOnboardingInfo({
        ...onboardingInfo,
        employee_form_id: data?.employee_form_id,
        form_id: data?.form_id,
      }),
    );
    callback && callback();
  } catch (e) {
    yield put(getDocumentsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while getting documents!"));
  }
}

export function* uploadCommonDocumentSaga({ payload, callback }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/hr-upload-common-document/${id}`,
      formData,
    );
    yield put(successAlert("Documents updated successfully!"));
    yield put(uploadCommonDocumentSuccess());
    callback && callback(data);
  } catch (e) {
    yield put(uploadCommonDocumentFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while uploading common document!"));
  }
}

export function* getCommonDocumentsSaga({ payload, callback }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/employee/${payload}/onboarding/common-document`,
    );
    yield put(getCommonDocumentsSuccess());
    callback && callback(data ?? []);
  } catch (e) {
    yield put(getCommonDocumentsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while getting documents!"));
  }
}

export function* downloadDocSaga({ payload, callback }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const data = yield call(
      api(token, null, false, true).post,
      `/api/employee/onboarding/download-file`,
      payload.formData,
    );
    if (data) {
      yield put(downloadDocSuccess(data));
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${payload?.title}.${payload?.type}`);
      document.body.appendChild(link);
      link.click();
    }
  } catch (e) {
    yield put(downloadDocFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while downloading document!"));
  }
}

export function* createCustomerFlow() {
  yield takeEvery(CREATE_CUSTOMER, createCustomerSaga);
}
export function* uploadDocumentFlow() {
  yield takeEvery(UPLOAD_DOCUMENT, uploadDocumentSaga);
}
export function* updatePeriodInfoFlow() {
  yield takeEvery(UPDATE_PERIOD_INFO, updatePeriodInfoSaga);
}
export function* setPermissionsFlow() {
  yield takeEvery(SET_PERMISSIONS, setPermissionsSaga);
}
export function* setAllowanceFlow() {
  yield takeEvery(SET_ALLOWANCE, setAllowanceSaga);
}
export function* onboardEmployeeFlow() {
  yield takeEvery(ONBOARD_EMPLOYEE, onboardEmployeeSaga);
}
export function* updateBasicInfoFlow() {
  yield takeEvery(UPDATE_BASIC_INFO, updateBasicInfoSaga);
}
export function* getDocumentsFlow() {
  yield takeEvery(GET_DOCUMENTS, getDocumentsSaga);
}
export function* uploadCommonDocumentFlow() {
  yield takeEvery(UPLOAD_COMMON_DOCUMENT, uploadCommonDocumentSaga);
}
export function* getCommonDocumentsFlow() {
  yield takeEvery(GET_COMMON_DOCUMENTS, getCommonDocumentsSaga);
}
export function* downloadDocFlow() {
  yield takeEvery(DOWNLOAD_DOC, downloadDocSaga);
}
export default function* OnboardingSaga() {
  yield all([
    createCustomerFlow(),
    uploadDocumentFlow(),
    updatePeriodInfoFlow(),
    setPermissionsFlow(),
    setAllowanceFlow(),
    onboardEmployeeFlow(),
    updateBasicInfoFlow(),
    getDocumentsFlow(),
    uploadCommonDocumentFlow(),
    getCommonDocumentsFlow(),
    downloadDocFlow(),
  ]);
}
